<!-- Main Container -->
<div class="min-h-screen bg-surface-ground">
  <!-- Hero Section -->
  <section class="hero">
    <div class="hero-content">
      <div class="container">
        <div class="hero-text">
          <div class="hero-badge">
            <span class="text-sm text-white font-medium">Version 1.1 – Updated February 19, 2025</span>
          </div>
          <h1 class="hero-title">
            Industry Intelligence Inc.<br />
            Brand Guidelines
          </h1>
          <p class="hero-subtitle">
            A comprehensive guide to our visual identity, voice, and values that shape how we present ourselves to the world.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- Main Content Area -->
  <div class="content-wrapper">
    <!-- Mobile Menu Toggle -->
    <button 
      class="mobile-menu-toggle lg:hidden"
      (click)="toggleToc()"
      aria-label="Toggle Table of Contents"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16m-7 6h7"/>
      </svg>
    </button>

    <!-- Table of Contents -->
    <nav 
      class="toc-container hidden lg:block"
      [class.active]="isTocVisible"
      aria-label="Table of Contents"
    >
      <div class="flex items-center justify-between mb-8">
        <h2 class="text-2xl font-display font-semibold">Contents</h2>
        <button 
          class="lg:hidden p-2 rounded-lg hover:bg-surface-hover"
          (click)="toggleToc()"
          aria-label="Close Table of Contents"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <ul class="space-y-2">
        @for (section of sections; track section.id) {
          <li>
            <a 
              (click)="scrollToSection(section.id)"
              class="nav-link"
              [class.active]="activeSection === section.id"
              [attr.aria-current]="activeSection === section.id ? 'true' : null"
            >
              {{ section.title }}
            </a>
          </li>
        }
      </ul>
    </nav>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Introduction -->
      <section id="introduction" class="section-card">
        <h2>Introduction</h2>
        <p>
          For over 25 years, Industry Intelligence Inc. has been a leader in delivering relevant market intelligence solutions across various industries. Our brand represents trust, innovation, and expertise. This document outlines the visual and verbal elements that constitute our brand identity, ensuring a unified and professional presence in all interactions.
        </p>
      </section>

      <!-- Brand Core -->
      <section id="brand-core" class="section-card">
        <h2>Brand Core</h2>
        <div class="grid md:grid-cols-2 gap-8 mb-12">
          <div class="feature-card">
            <div class="icon-container">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            </div>
            <h3>Mission Statement</h3>
            <p>
              To provide business professionals with relevant, actionable market intelligence that empowers informed decision-making and fosters business growth.
            </p>
          </div>

          <div class="feature-card">
            <div class="icon-container">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3>Vision Statement</h3>
            <p>
              To be the leading provider of integrated market intelligence solutions, enabling companies to navigate complex market dynamics with confidence.
            </p>
          </div>
        </div>

        <h3>Brand Values</h3>
        <div class="grid sm:grid-cols-2 gap-8">
          <div class="value-card">
            <h4>Innovation</h4>
            <p>Continuously evolving to meet the changing needs of our clients.</p>
          </div>
          <div class="value-card">
            <h4>Integrity</h4>
            <p>Maintaining the highest ethical standards in all our operations.</p>
          </div>
          <div class="value-card">
            <h4>Collaboration</h4>
            <p>Partnering with clients to deliver tailored solutions.</p>
          </div>
          <div class="value-card">
            <h4>Excellence</h4>
            <p>Striving for the highest quality in our services and products.</p>
          </div>
        </div>
      </section>

      <!-- Brand Identity -->
      <section id="brand-identity" class="section-card">
        <h2>Brand Identity</h2>
        
        <!-- Logo Usage -->
        <div class="mb-12">
          <h3>Logo Usage</h3>
          <p class="mb-6">Our logo features a stylized globe with three letter "I"s integrated within it, symbolizing global market intelligence. This emblem is centrally positioned between the words "Industry" and "Intelligence Inc."</p>
          
          <div class="grid md:grid-cols-2 gap-8">
            <div class="feature-card">
              <img src="/assets/brand/logo-light.svg" alt="Industry Intelligence Logo Light" class="w-full h-auto">
              <p class="text-center mt-4 font-medium">Primary Logo (Monochromatic Gray #58595b)</p>
            </div>
            <div class="feature-card bg-gray-900">
              <img src="/assets/brand/logo-dark.svg" alt="Industry Intelligence Logo Dark" class="w-full h-auto">
              <p class="text-center mt-4 font-medium text-white">White Version (For Dark Backgrounds)</p>
            </div>
          </div>

          <div class="mt-8">
            <h4>Logo Guidelines</h4>
            <ul class="list-disc pl-6 space-y-2">
              <li>Maintain a minimum clear space around the logo equivalent to 20% of its height.</li>
              <li>Minimum size: Print - 0.55 inches (14 mm), Digital - 52 pixels width</li>
              <li>Do not distort, rotate, or flip the logo</li>
              <li>Do not alter colors or apply effects</li>
              <li>Do not use the logo on low-contrast backgrounds</li>
              <li>Do not add shadows, gradients, or outlines</li>
            </ul>
          </div>
        </div>

        <!-- Color Palette -->
        <div class="mb-12">
          <h3>Color Palette</h3>
          
          <!-- Primary Colors -->
          <h4 class="text-xl font-semibold mb-4">Primary Colors</h4>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div class="color-card">
              <div class="color-preview" style="background-color: #333333"></div>
              <div class="p-4">
                <p class="font-medium">Charcoal Gray</p>
                <p class="text-sm font-mono text-gray-500">#333333</p>
                <p class="text-sm mt-2">Primary brand color</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #003366"></div>
              <div class="p-4">
                <p class="font-medium">Deep Blue</p>
                <p class="text-sm font-mono text-gray-500">#003366</p>
                <p class="text-sm mt-2">Primary accent</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #4F46E5"></div>
              <div class="p-4">
                <p class="font-medium">Indigo</p>
                <p class="text-sm font-mono text-gray-500">#4F46E5</p>
                <p class="text-sm mt-2">Interactive elements</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #312E81"></div>
              <div class="p-4">
                <p class="font-medium">Dark Indigo</p>
                <p class="text-sm font-mono text-gray-500">#312E81</p>
                <p class="text-sm mt-2">Hover states</p>
              </div>
            </div>
          </div>

          <!-- Secondary Colors -->
          <h4 class="text-xl font-semibold mb-4">Secondary Colors</h4>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div class="color-card">
              <div class="color-preview" style="background-color: #E5E5E5"></div>
              <div class="p-4">
                <p class="font-medium">Light Gray</p>
                <p class="text-sm font-mono text-gray-500">#E5E5E5</p>
                <p class="text-sm mt-2">Backgrounds</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #F3F4F6"></div>
              <div class="p-4">
                <p class="font-medium">Off White</p>
                <p class="text-sm font-mono text-gray-500">#F3F4F6</p>
                <p class="text-sm mt-2">Surface areas</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #6B7280"></div>
              <div class="p-4">
                <p class="font-medium">Cool Gray</p>
                <p class="text-sm font-mono text-gray-500">#6B7280</p>
                <p class="text-sm mt-2">Secondary text</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #D1D5DB"></div>
              <div class="p-4">
                <p class="font-medium">Border Gray</p>
                <p class="text-sm font-mono text-gray-500">#D1D5DB</p>
                <p class="text-sm mt-2">Borders, dividers</p>
              </div>
            </div>
          </div>

          <!-- Accent Colors -->
          <h4 class="text-xl font-semibold mb-4">Accent Colors</h4>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div class="color-card">
              <div class="color-preview" style="background-color: #3B82F6"></div>
              <div class="p-4">
                <p class="font-medium">Blue</p>
                <p class="text-sm font-mono text-gray-500">#3B82F6</p>
                <p class="text-sm mt-2">Links, buttons</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #60A5FA"></div>
              <div class="p-4">
                <p class="font-medium">Light Blue</p>
                <p class="text-sm font-mono text-gray-500">#60A5FA</p>
                <p class="text-sm mt-2">Highlights</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #818CF8"></div>
              <div class="p-4">
                <p class="font-medium">Periwinkle</p>
                <p class="text-sm font-mono text-gray-500">#818CF8</p>
                <p class="text-sm mt-2">Decorative elements</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #6366F1"></div>
              <div class="p-4">
                <p class="font-medium">Bright Indigo</p>
                <p class="text-sm font-mono text-gray-500">#6366F1</p>
                <p class="text-sm mt-2">Call to action</p>
              </div>
            </div>
          </div>

          <!-- Functional Colors -->
          <h4 class="text-xl font-semibold mb-4">Functional Colors</h4>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div class="color-card">
              <div class="color-preview" style="background-color: #22C55E"></div>
              <div class="p-4">
                <p class="font-medium">Success</p>
                <p class="text-sm font-mono text-gray-500">#22C55E</p>
                <p class="text-sm mt-2">Positive states</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #EAB308"></div>
              <div class="p-4">
                <p class="font-medium">Warning</p>
                <p class="text-sm font-mono text-gray-500">#EAB308</p>
                <p class="text-sm mt-2">Cautionary states</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #EF4444"></div>
              <div class="p-4">
                <p class="font-medium">Error</p>
                <p class="text-sm font-mono text-gray-500">#EF4444</p>
                <p class="text-sm mt-2">Error states</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background-color: #3B82F6"></div>
              <div class="p-4">
                <p class="font-medium">Info</p>
                <p class="text-sm font-mono text-gray-500">#3B82F6</p>
                <p class="text-sm mt-2">Information states</p>
              </div>
            </div>
          </div>

          <!-- Gradient Examples -->
          <h4 class="text-xl font-semibold mt-12 mb-4">Brand Gradients</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="color-card">
              <div class="color-preview" style="background: linear-gradient(135deg, #4F46E5 0%, #3B82F6 100%)"></div>
              <div class="p-4">
                <p class="font-medium">Primary Gradient</p>
                <p class="text-sm font-mono text-gray-500">135° / Indigo to Blue</p>
                <p class="text-sm mt-2">Hero sections, CTAs</p>
              </div>
            </div>
            <div class="color-card">
              <div class="color-preview" style="background: linear-gradient(135deg, #312E81 0%, #1E40AF 100%)"></div>
              <div class="p-4">
                <p class="font-medium">Dark Gradient</p>
                <p class="text-sm font-mono text-gray-500">135° / Dark Indigo to Dark Blue</p>
                <p class="text-sm mt-2">Dark mode elements</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Typography -->
        <div class="mb-12">
          <h3>Typography</h3>
          <div class="space-y-8">
            <div class="typography-card">
              <h4>Open Sans (Primary Typeface)</h4>
              <div class="space-y-4">
                <p>Primary typeface for digital and print communications</p>
                <p class="font-normal">Regular weight for body text</p>
                <p class="font-semibold">Semi-bold for headings</p>
                <p class="font-bold">Bold for strong emphasis</p>
              </div>
            </div>
            
            <div class="typography-card">
              <h4>Roboto (Secondary Typeface)</h4>
              <div class="space-y-4">
                <p>Alternative typeface where Open Sans is unavailable</p>
                <p class="font-normal">Regular weight for body text</p>
                <p class="font-medium">Medium for subheadings</p>
                <p class="font-semibold">Semi-bold for emphasis</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Imagery and Graphics -->
        <div>
          <h3>Imagery and Graphics</h3>
          <div class="space-y-4">
            <div class="feature-card">
              <h4>Style Guidelines</h4>
              <ul class="list-disc pl-6 space-y-2">
                <li>Use high-quality images depicting data, networks, and global connections</li>
                <li>Imagery should align with the brand palette, using grayscale with blue accents</li>
                <li>Clean, vector-based designs should be used to maintain a professional aesthetic</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <!-- Brand Personality -->
      <section id="brand-personality" class="section-card">
        <h2>Brand Personality</h2>
        
        <div class="mb-8">
          <h3>Tone of Voice</h3>
          <ul class="list-disc pl-6 space-y-2">
            <li>Professional yet conversational</li>
            <li>Informative, engaging, and authoritative</li>
            <li>Clear and concise, avoiding unnecessary jargon</li>
          </ul>
        </div>

        <div>
          <h3>Language Guidelines</h3>
          <ul class="list-disc pl-6 space-y-2">
            <li>Follow standard American English grammar rules</li>
            <li>Ensure industry-specific terms are used appropriately and accessibly</li>
            <li>Maintain a balance between technical accuracy and reader-friendliness</li>
          </ul>
        </div>
      </section>

      <!-- Audience Personas -->
      <section id="audience-personas" class="section-card">
        <h2>Audience Personas</h2>
        
        <div class="mb-8">
          <h3>Target Audience Profiles</h3>
          <div class="grid sm:grid-cols-2 gap-8">
            <div class="value-card">
              <h4>Corporate Executives</h4>
              <p>Seeking strategic insights</p>
            </div>
            <div class="value-card">
              <h4>Market Analysts</h4>
              <p>Evaluating market trends and data</p>
            </div>
            <div class="value-card">
              <h4>Product Managers</h4>
              <p>Assessing market positioning</p>
            </div>
            <div class="value-card">
              <h4>Sales Teams</h4>
              <p>Understanding market dynamics for sales strategy</p>
            </div>
          </div>
        </div>

        <div>
          <h3>Customer Journeys</h3>
          <div class="grid md:grid-cols-3 gap-8">
            <div class="feature-card">
              <h4>Awareness</h4>
              <p>Discovery via digital marketing, events, and referrals</p>
            </div>
            <div class="feature-card">
              <h4>Consideration</h4>
              <p>Engagement with blogs, webinars, and case studies</p>
            </div>
            <div class="feature-card">
              <h4>Decision</h4>
              <p>Direct consultations and personalized demonstrations</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Application Guidelines -->
      <section id="application-guidelines" class="section-card">
        <h2>Application Guidelines</h2>
        
        <div class="mb-8">
          <h3>Digital Applications</h3>
          <ul class="list-disc pl-6 space-y-2">
            <li>Maintain a clean, intuitive interface across all digital platforms</li>
            <li>Ensure WCAG compliance for accessibility</li>
          </ul>
        </div>

        <div>
          <h3>Environmental Branding</h3>
          <ul class="list-disc pl-6 space-y-2">
            <li>Office branding should reflect the company's colors and professional aesthetic</li>
            <li>Event booths should use branded elements to ensure consistency</li>
          </ul>
        </div>
      </section>

      <!-- Social Media Strategy -->
      <section id="social-media" class="section-card">
        <h2>Social Media Strategy</h2>
        
        <div class="mb-8">
          <h3>Platform Guidelines</h3>
          <ul class="list-disc pl-6 space-y-2">
            <li>Maintain brand voice and tone across all platforms</li>
            <li>Use the primary logo for all profile images</li>
            <li>Engage actively with audiences through thought leadership content</li>
          </ul>
        </div>

        <div>
          <h3>Content Guidelines</h3>
          <ul class="list-disc pl-6 space-y-2">
            <li>Post industry insights, data trends, and company updates</li>
            <li>Ensure posts align with brand messaging and design standards</li>
          </ul>
        </div>
      </section>

      <!-- Legal and Compliance -->
      <section id="legal" class="section-card">
        <h2>Legal and Compliance</h2>
        
        <div class="mb-8">
          <h3>Trademark Usage</h3>
          <ul class="list-disc pl-6 space-y-2">
            <li>The company logo and tagline are trademarked</li>
            <li>Proper usage is required to protect brand integrity</li>
          </ul>
        </div>

        <div>
          <h3>Compliance Standards</h3>
          <p>Adhere to industry regulations in all communications</p>
        </div>
      </section>

      <!-- Internal Brand Engagement -->
      <section id="internal-brand" class="section-card">
        <h2>Internal Brand Engagement</h2>
        
        <div class="mb-8">
          <h3>Employee Communication</h3>
          <p>Employees should align with brand values in internal and external communications</p>
        </div>

        <div>
          <h3>Brand Training Resources</h3>
          <p>Ongoing training ensures employees represent the brand accurately</p>
        </div>
      </section>

      <!-- Accessibility Standards -->
      <section id="accessibility" class="section-card">
        <h2>Accessibility Standards</h2>
        <ul class="list-disc pl-6 space-y-2">
          <li>Implement WCAG 2.1 guidelines for digital content</li>
          <li>Ensure inclusivity in brand messaging and design</li>
        </ul>
      </section>

      <!-- Crisis Communication -->
      <section id="crisis" class="section-card">
        <h2>Crisis Communication Guidelines</h2>
        
        <div class="mb-8">
          <h3>Protocol</h3>
          <p>Establish procedures to maintain brand integrity during crises</p>
        </div>

        <div>
          <h3>Messaging Framework</h3>
          <p>Provide templates and tone guidelines for crisis communications</p>
        </div>
      </section>

      <!-- Measurement and Evaluation -->
      <section id="measurement" class="section-card">
        <h2>Measurement and Evaluation</h2>
        
        <div class="mb-8">
          <h3>Brand Performance Metrics</h3>
          <p>Define KPIs to assess brand consistency and effectiveness</p>
        </div>

        <div>
          <h3>Feedback Mechanisms</h3>
          <p>Collect and analyze stakeholder feedback for continuous improvement</p>
        </div>
      </section>

      <!-- Contact Information -->
      <section id="contact" class="section-card">
        <h2>Contact Information</h2>
        <div class="space-y-4">
          <p class="font-medium">Brand Management Team</p>
          <p>Industry Intelligence Inc.</p>
          <p>1990 S Bundy Dr. Suite #380</p>
          <p>Los Angeles, CA 90025</p>
          <p>Phone: +1 (310) 553 0008</p>
          <p>Email: <a href="mailto:contact&#64;industryintel.com">contact&#64;industryintel.com</a></p>
          <p>Website: <a href="https://www.industryintel.com" target="_blank" rel="noopener">www.industryintel.com</a></p>
        </div>
      </section>
    </div>
  </div>
</div> 